import { isNonEmpty } from '@infinex-private/ts-utils/array';

import type { AllChainKey } from './allChains';
import { allChains } from './allChains';
import { NATIVE_IMPORTED_WALLET_CHAIN_RUNTIMES } from './configTypes';
import type {
  AllChainConfig,
  ChainLimits,
  ChainRuntime,
  ImportedWalletChainConfig,
  ImportedWalletChainKey,
  ImportedWalletChainRuntime,
  ChainDecoration,
  NativeImportedWalletChainKey,
  NativeImportedWalletChainConfig,
  NativeImportedWalletChainRuntime,
} from './configTypes';
import { chains } from './derivedConstants';
import type {
  ChainConfig,
  ChainKey,
  Chains,
  EvmChainConfig,
  EvmChainKey,
  EvmChains,
  EvmMainnetChainKey,
  EvmVaultChainKey,
  MainnetChainKey,
  MainnetChains,
  MainnetVaultChainKey,
  MainnetVaultChains,
  MainnetWalletChainKey,
  MainnetWalletChains,
  NativeChainKey,
  NativeChains,
  NativeImportedWalletChains,
  NativeMainnetWalletChainKey,
  NativeMainnetWalletChains,
  SvmChainKey,
  SvmChains,
  SvmMainnetChainKey,
  SvmVaultChainKey,
  VaultChainKey,
  VaultChains,
  WalletChainKey,
  WalletChains,
} from './derivedTypes';
import { CHAIN_RUNTIME_CONFIG } from './runtimes';

import { currentEnv } from '../env';
import type { DefuseChainId } from '../intents';
import { isDefuseChainId, NEAR_INTENTS_CHAIN_METADATA } from '../intents';

export function isChainKey(chainKey: unknown): chainKey is ChainKey {
  return typeof chainKey === 'string' && chainKey in chains;
}
export function isEvmChainKey(chainKey: unknown): chainKey is EvmChainKey {
  return isChainKey(chainKey) && chains[chainKey].runtime === 'evm';
}
export function makeChainKey(maybeChainKey: string): ChainKey {
  if (!isChainKey(maybeChainKey)) {
    throw new Error(`Invalid chain key: ${maybeChainKey}`);
  }
  return maybeChainKey as ChainKey;
}

// Service helpers ----------------------------------------

type ChainResolution = {
  chainKey: AllChainKey;
  chainConfig: AllChainConfig<ChainRuntime, boolean>;
};

// Supported Zerion chains are available from the API; https://developers.zerion.io/reference/listchains
export function resolveZerionChain(
  zerionChain: string
): ChainResolution | null {
  const matches = Object.entries(allChains).filter(
    ([_, chain]) => chain.services.zerion?.chainKey === zerionChain
  );
  if (!isNonEmpty(matches)) return null;
  if (matches.length > 1) {
    console.error(`ChainMultiple chains found for Zerion chain ${zerionChain}`);
  }
  const [chainKey, chainConfig] = matches[0];
  return { chainKey, chainConfig } as ChainResolution;
}

// QuickNode have "chain keys" and "network keys"; _Sometimes_ they're different. Thanks QuickNode.
export function resolveQnStreamNetwork(
  network: string
): ChainResolution | null {
  const matches = Object.entries(allChains).filter(
    ([_, chain]) => chain.services.qnStreams?.networkKey === network
  );
  if (!isNonEmpty(matches)) return null;
  if (matches.length > 1) {
    console.error(`Multiple chains found for QuickNode network ${network}`);
  }
  const [chainKey, chainConfig] = matches[0];
  return { chainKey, chainConfig } as ChainResolution;
}

// External API / helpers ----------------------------------------

type FilterFn = (c: ChainConfig) => boolean;

// Generic functions to get config objects, keys, etc.
function makeGetChains<ChainsT>(filterFn: FilterFn) {
  return function (): ChainsT {
    return Object.fromEntries(
      Object.entries(chains).filter(([_, c]) => filterFn(c))
    ) as ChainsT;
  };
}
function makeGetChainKeys<KeyT extends ChainKey>(filterFn: FilterFn) {
  // The pattern used here for the return type allows the value to be used with Zod's .enum() method
  return function (): [KeyT, ...KeyT[]] {
    return Object.entries(chains)
      .filter(([_, c]) => filterFn(c))
      .map(([key]) => key) as [KeyT, ...KeyT[]];
  };
}
// Accepts any ChainKey and reduces the type of the argument to KeyT (if successful)
function makeIsChainKeys<KeyT extends ChainKey>(filterFn: FilterFn) {
  return function (key: ChainKey): key is KeyT {
    return chains[key] && filterFn(chains[key]);
  };
}

// Active chains
const aFn = (c: ChainConfig) => c.environments[currentEnv()];
export const getActiveChains = (): Partial<Chains> =>
  makeGetChains<Chains>(aFn)();
export const getActiveChainKeys = makeGetChainKeys<ChainKey>(aFn);
export const isActiveChainKey = makeIsChainKeys<ChainKey>(aFn);

// Active with wallet support
const awFn = (c: ChainConfig) =>
  c.environments[currentEnv()] && c.features.wallet.supported;
export const getActiveWalletChains = (): Partial<WalletChains> =>
  makeGetChains<WalletChains>(awFn)();
export const getActiveWalletChainKeys = makeGetChainKeys<WalletChainKey>(awFn);
export const isActiveWalletChainKey = makeIsChainKeys<WalletChainKey>(awFn);

// These helpers filter by `import: true` and are intended ONLY for the import
// flow (deriving new addresses, showing importable chains in the UI).
// Do NOT use them to gate post-import functionality (swidge, send, portfolio).
// Imported Infinex SCAs can exist on chains with `import: false` and must
// remain usable after import. Use the wallet's actual addresses instead.
const isActiveWithImportedWalletSupport = (c: ChainConfig) =>
  c.environments[currentEnv()] &&
  c.features.wallet.supported &&
  c.features.wallet.import;
export const getActiveWalletImportChains = (): Partial<WalletChains> =>
  makeGetChains<WalletChains>(isActiveWithImportedWalletSupport)();
export const getActiveImportWalletChainKeys = makeGetChainKeys<WalletChainKey>(
  isActiveWithImportedWalletSupport
);
export const isActiveImportWalletChainKey = makeIsChainKeys<WalletChainKey>(
  isActiveWithImportedWalletSupport
);

const isActiveWithNativeImportedWalletSupport = (c: ChainConfig) =>
  c.environments[currentEnv()] &&
  c.features.wallet.supported &&
  c.features.wallet.import &&
  (NATIVE_IMPORTED_WALLET_CHAIN_RUNTIMES as readonly ChainRuntime[]).includes(
    c.runtime
  );
export const getActiveWalletNativeImportChains =
  makeGetChains<NativeImportedWalletChains>(
    isActiveWithNativeImportedWalletSupport
  );
export const getActiveNativeImportWalletChainKeys =
  makeGetChainKeys<NativeImportedWalletChainKey>(
    isActiveWithNativeImportedWalletSupport
  );
export const isActiveNativeImportWalletChainKey =
  makeIsChainKeys<NativeImportedWalletChainKey>(
    isActiveWithNativeImportedWalletSupport
  );

// Active with vault support
const avFn = (c: ChainConfig) =>
  c.environments[currentEnv()] && c.features.vault.supported;
export const getActiveVaultChains = (): Partial<VaultChains> =>
  makeGetChains<VaultChains>(avFn)();
export const getActiveVaultChainKeys = makeGetChainKeys<VaultChainKey>(avFn);
export const isActiveVaultChainKey = makeIsChainKeys<VaultChainKey>(avFn);

// Active EVM chains
const aeFn = (c: ChainConfig) =>
  c.environments[currentEnv()] && c.runtime === 'evm';
export const getActiveEvmChains = (): Partial<EvmChains> =>
  makeGetChains<EvmChains>(aeFn)();
export const getActiveEvmChainKeys = makeGetChainKeys<EvmChainKey>(aeFn);
export const isActiveEvmChainKey = makeIsChainKeys<EvmChainKey>(aeFn);

// Active EVM mainnet chains
const aemFn = (c: ChainConfig) =>
  c.environments[currentEnv()] && c.runtime === 'evm' && c.isMainnet;
export const getActiveEvmMainnetChains = makeGetChains<EvmChains>(aemFn);
export const getActiveEvmMainnetChainKeys =
  makeGetChainKeys<EvmMainnetChainKey>(aemFn);
export const isActiveEvmMainnetChainKey =
  makeIsChainKeys<EvmMainnetChainKey>(aemFn);

// Active EVM chains with vault support
const aevFn = (c: ChainConfig) =>
  c.environments[currentEnv()] &&
  c.runtime === 'evm' &&
  c.features.vault.supported;
export const getActiveEvmVaultChains = makeGetChains<EvmChains>(aevFn);
export const getActiveEvmVaultChainKeys =
  makeGetChainKeys<EvmVaultChainKey>(aevFn);
export const isActiveEvmVaultChainKey =
  makeIsChainKeys<EvmVaultChainKey>(aevFn);

// Active SVM chains
const asFn = (c: ChainConfig) =>
  c.environments[currentEnv()] && c.runtime === 'svm';
export const getActiveSvmChains = (): Partial<SvmChains> =>
  makeGetChains<SvmChains>(asFn)();
export const getActiveSvmChainKeys = makeGetChainKeys<SvmChainKey>(asFn);
export const isActiveSvmChainKey = makeIsChainKeys<SvmChainKey>(asFn);

// Active SVM mainnet chains
const asmFn = (c: ChainConfig) =>
  c.environments[currentEnv()] && c.runtime === 'svm' && c.isMainnet;
export const getActiveSvmMainnetChains = makeGetChains<SvmChains>(asmFn);
export const getActiveSvmMainnetChainKeys =
  makeGetChainKeys<SvmMainnetChainKey>(asmFn);
export const isActiveSvmMainnetChainKey =
  makeIsChainKeys<SvmMainnetChainKey>(asmFn);

// Active SVM chains with vault support
const asvFn = (c: ChainConfig) =>
  c.environments[currentEnv()] &&
  c.runtime === 'svm' &&
  c.features.vault.supported;
export const getActiveSvmVaultChains = makeGetChains<SvmChains>(asvFn);
export const getActiveSvmVaultChainKeys =
  makeGetChainKeys<SvmVaultChainKey>(asvFn);
export const isActiveSvmVaultChainKey =
  makeIsChainKeys<SvmVaultChainKey>(asvFn);

// Active native chainkeys
const anFn = (c: ChainConfig) =>
  c.environments[currentEnv()] &&
  (c.runtime === 'evm' ||
    c.runtime === 'svm' ||
    c.runtime === 'sui' ||
    c.runtime === 'near');
export const getActiveNativeChains = makeGetChains<NativeChains>(anFn);
export const getActiveNativeChainKeys = makeGetChainKeys<NativeChainKey>(anFn);
export const isActiveNativeChainKey = makeIsChainKeys<NativeChainKey>(anFn);

// Active mainnet chainkeys
const amFn = (c: ChainConfig) => c.environments[currentEnv()] && c.isMainnet;
export const getActiveMainnetChains = makeGetChains<MainnetChains>(amFn);
export const getActiveMainnetChainKeys =
  makeGetChainKeys<MainnetChainKey>(amFn);
export const isActiveMainnetChainKey = makeIsChainKeys<MainnetChainKey>(amFn);

// Active mainnet Vault chainkeys
const amvFn = (c: ChainConfig) =>
  c.environments[currentEnv()] && c.isMainnet && c.features.vault.supported;
export const getActiveMainnetVaultChains =
  makeGetChains<MainnetVaultChains>(amvFn);
export const getActiveMainnetVaultChainKeys =
  makeGetChainKeys<MainnetVaultChainKey>(amvFn);
export const isActiveMainnetVaultChainKey =
  makeIsChainKeys<MainnetVaultChainKey>(amvFn);

// Active Mainnet Wallet chainkeys
const amwFn = (c: ChainConfig) =>
  c.environments[currentEnv()] && c.isMainnet && c.features.wallet.supported;
export const getActiveMainnetWalletChains =
  makeGetChains<MainnetWalletChains>(amwFn);
export const getActiveMainnetWalletChainKeys =
  makeGetChainKeys<MainnetWalletChainKey>(amwFn);

// Active Native Mainnet Wallet chainkeys
const anmwFn = (c: ChainConfig) =>
  c.environments[currentEnv()] &&
  c.isMainnet &&
  c.features.wallet.supported &&
  (c.runtime === 'evm' ||
    c.runtime === 'svm' ||
    c.runtime === 'sui' ||
    c.runtime === 'near');
export const getActiveNativeMainnetWalletChains =
  makeGetChains<NativeMainnetWalletChains>(anmwFn);
export const getActiveNativeMainnetWalletChainKeys =
  makeGetChainKeys<NativeMainnetWalletChainKey>(anmwFn);
export const isActiveNativeMainnetWalletChainKey =
  makeIsChainKeys<NativeMainnetWalletChainKey>(anmwFn);

// Property accessors ----------------------------------------

/** Get the display name for a given chain key, or defuse chain ID. */
export function getDisplayName(chainKey: ChainKey | DefuseChainId): string {
  if (isChainKey(chainKey)) {
    return chains[chainKey].displayName;
  }
  if (isDefuseChainId(chainKey)) {
    return NEAR_INTENTS_CHAIN_METADATA[chainKey].displayName;
  }

  throw new Error(`Chain ${chainKey} not found`);
}

/** Get the decorative properties for a given chain key, or defuse chain ID. */
export function getDecoration(
  chainKey: ChainKey | DefuseChainId
): ChainDecoration {
  if (isChainKey(chainKey)) {
    return chains[chainKey].decoration;
  }
  if (isDefuseChainId(chainKey)) {
    return chains['nearIntents'].decoration; // TODO: We currently only have one decoration for NEAR intents, not its "subchains" like BTC
  }
  throw new Error(`Chain ${chainKey} not found`);
}

/**
 * Get chain-specific limits for the current environment
 * @param chainKey The chain to get limits for
 * @returns The chain's limits configuration
 * @throws If chain environment is not set or chain is not found
 */
export function getChainLimits(chainKey: ChainKey): ChainLimits {
  const chain = getActiveChains()[chainKey];
  if (!chain) {
    throw new Error(`Chain ${chainKey} not found or not active`);
  }
  if (!chain?.limits) {
    throw new Error(`Chain ${chainKey} has invalid limits configuration`);
  }
  return chain.limits;
}

/**
 * Get swidge limits for all active wallet chains in current environment
 * @returns Record of wallet chain keys to their swidge-specific limits
 * @throws If chain environment is not set
 */
export function getWalletSwidgeLimits(): Record<
  ChainKey,
  {
    minUsdSwidgeFromAmount: number;
    maxUsdSwidgeFromAmount: number;
    maxUsdSwidgePlatformFeePerBps: number;
  }
> {
  return Object.fromEntries(
    Object.entries(getActiveChains())
      .filter(([_, config]) => config.features.wallet.supported)
      .map(([key, config]) => [
        key,
        {
          minUsdSwidgeFromAmount: config.limits.minUsdSwidgeFromAmount,
          maxUsdSwidgeFromAmount: config.limits.maxUsdSwidgeFromAmount,
          maxUsdSwidgePlatformFeePerBps:
            config.limits.maxUsdSwidgePlatformFeePerBps,
        },
      ])
  ) as Record<
    ChainKey,
    {
      minUsdSwidgeFromAmount: number;
      maxUsdSwidgeFromAmount: number;
      maxUsdSwidgePlatformFeePerBps: number;
    }
  >;
}

export function isEip7702Supported(chainKey: ChainKey): boolean {
  const chain = getActiveChains()[chainKey];
  if (!chain?.features.wallet.supported) {
    return false;
  }
  return chain.features.wallet.eip7702 === true;
}

/**
 * Whether Infinex Safe create/deploy/send is enabled for this chain.
 *
 * Requires Safe Transaction Service config (`services.safe`) and EIP-7702.
 * EIP-7702 is required for now so the app-wallet address users pick is the
 * EOA operation key — on non-7702 chains that address is an SCA, which is not
 * a usable Safe owner under our signing model. Relax later if owner selection
 * always resolves SCAs to the operation key.
 */
export function isSafeSupported(chainKey: ChainKey): boolean {
  const chain = allChains[chainKey];
  if (!chain?.services.safe) return false;
  if (!('viemChain' in chain) || !chain.viemChain) return false;
  if (!('features' in chain)) return false;

  const wallet = chain.features.wallet;
  return (
    wallet.supported === true && 'eip7702' in wallet && wallet.eip7702 === true
  );
}

export function getActiveEip7702SupportedChains(): [ChainKey, ChainConfig][] {
  return Object.entries(getActiveChains()).filter(([chainKey]) =>
    isEip7702Supported(chainKey as ChainKey)
  ) as [ChainKey, ChainConfig][];
}

/**
 * Get the EVM chain key for a given chain ID
 * @param chainId - The EVM chain ID to look up
 * @returns The matching EVM chain key or undefined if not found
 */
export function getEvmChainKeyByChainId(
  chainId: number
): EvmChainKey | undefined {
  const chainEntry = Object.entries(getActiveEvmChains()).find(
    ([_, config]) => config.viemChain.id === chainId
  );
  return chainEntry?.[0] as EvmChainKey | undefined;
}

// Defuse asset identifier resolution ----------------------------------------

/** EVM numeric chain id → supported chain key, derived from the registry. */
const EVM_CHAIN_ID_TO_SUPPORTED_CHAIN_KEY: ReadonlyMap<number, EvmChainKey> =
  new Map(
    Object.entries(chains)
      .filter(([_, c]) => c.runtime === 'evm')
      .map(([key, c]) => [
        (c as EvmChainConfig).viemChain.id,
        key as EvmChainKey,
      ])
  );

/**
 * The SUPPORTED chain key for an EVM numeric chain id — registry membership
 * only, unlike {@link getEvmChainKeyByChainId} which also requires the chain
 * to be active in the current environment. Map-backed, safe on hot paths.
 */
export function getSupportedEvmChainKeyByChainId(
  chainId: number
): EvmChainKey | undefined {
  return EVM_CHAIN_ID_TO_SUPPORTED_CHAIN_KEY.get(chainId);
}

/**
 * Non-EVM defuse CHAIN_TYPEs that map to a supported platform chain. EVM
 * chains resolve by numeric chain id instead, and the NEAR-Intents native
 * chains resolve as `DefuseChainId`s (see {@link resolveDefuseAssetChain}).
 */
const DEFUSE_CHAIN_TYPE_TO_CHAIN_KEY: Partial<Record<string, ChainKey>> = {
  sol: 'solana',
  near: 'near',
  sui: 'sui',
};

/**
 * Resolve a defuse asset identifier (`CHAIN_TYPE:CHAIN_ID[:ADDRESS]`) to the
 * SUPPORTED platform chain it lives on:
 *  - one of the six NEAR-Intents native chains → its `DefuseChainId`;
 *  - an EVM chain, matched by numeric chain id against the supported
 *    registry → its `ChainKey`;
 *  - Solana / NEAR / Sui, matched by chain type → its `ChainKey`;
 *  - anything else → `null`: bridge-only long-tail chains (Aptos / TON /
 *    Tron / Stellar / …), EVM ids the registry doesn't carry, malformed input.
 */
export function resolveDefuseAssetChain(
  defuseAssetIdentifier: string
): ChainKey | DefuseChainId | null {
  const [chainType, chainId] = defuseAssetIdentifier.split(':');
  if (!chainType || !chainId) return null;
  const prefix = `${chainType}:${chainId}`;
  if (isDefuseChainId(prefix)) return prefix;
  if (chainType === 'eth') {
    return EVM_CHAIN_ID_TO_SUPPORTED_CHAIN_KEY.get(Number(chainId)) ?? null;
  }
  return DEFUSE_CHAIN_TYPE_TO_CHAIN_KEY[chainType] ?? null;
}

/**
 * Whether a defuse asset identifier's chain is one the platform supports —
 * see {@link resolveDefuseAssetChain} for the resolution rules.
 */
export function isSupportedDefuseAssetChain(
  defuseAssetIdentifier: string
): boolean {
  return resolveDefuseAssetChain(defuseAssetIdentifier) !== null;
}

/**
 * Returns chains where `import: true`. Use ONLY in the import flow (deriving
 * new addresses, choosing which chains to import). Do NOT use to determine
 * which chains an already-imported wallet can transact on — imported Infinex
 * SCAs can exist on chains with `import: false`.
 *
 * @param activeChains The chains active in the calling context (frontend may be different from the worker due to feature flags)
 * @returns Object entries of the active chains that are relevant to imported wallets
 */
export function getImportChains(
  activeChains: Partial<Chains>
): [ImportedWalletChainKey, ImportedWalletChainConfig][] {
  const importChains = Object.entries(activeChains).filter(
    ([_, chain]) =>
      chain.features.wallet.supported && chain.features.wallet.import
  ) as [ImportedWalletChainKey, ImportedWalletChainConfig][];

  return importChains;
}

/**
 * Returns native chains where `import: true`. Same caveat as `getImportChains`:
 * use ONLY in the import flow, not for post-import chain validation.
 *
 * @param activeChains The native chains active in the calling context (frontend may be different from the worker due to feature flags)
 * @returns Object entries of the active native chains that are relevant to imported wallets
 */
export function getNativeImportChains(
  activeChains: Partial<Chains>
): [NativeImportedWalletChainKey, NativeImportedWalletChainConfig][] {
  const nativeImportChains = Object.entries(activeChains).filter(
    ([_, chain]) =>
      chain.features.wallet.supported &&
      chain.features.wallet.import &&
      (
        NATIVE_IMPORTED_WALLET_CHAIN_RUNTIMES as readonly ChainRuntime[]
      ).includes(chain.runtime)
  ) as [NativeImportedWalletChainKey, NativeImportedWalletChainConfig][];

  return nativeImportChains;
}

export function getImportChainRuntimes(
  activeChains: Partial<Chains>
): ImportedWalletChainRuntime[] {
  // Runtime filtering by import: true ensures only supported runtimes are returned
  return [
    ...new Set(
      getImportChains(activeChains).map(([_, chain]) => chain.runtime)
    ),
  ] as ImportedWalletChainRuntime[];
}

export function getNativeImportChainRuntimes(
  activeChains: Partial<Chains>
): NativeImportedWalletChainRuntime[] {
  // Runtime filtering by import: true ensures only supported runtimes are returned
  return [
    ...new Set(
      getNativeImportChains(activeChains).map(([_, chain]) => chain.runtime)
    ),
  ] as NativeImportedWalletChainRuntime[];
}

export function isNativeImportChainRuntime(
  runtime: ChainRuntime
): runtime is NativeImportedWalletChainRuntime {
  return (
    NATIVE_IMPORTED_WALLET_CHAIN_RUNTIMES as readonly ChainRuntime[]
  ).includes(runtime);
}

export function getPrimaryChainKeyForRuntime(runtime: ChainRuntime): ChainKey {
  return CHAIN_RUNTIME_CONFIG[runtime].primaryChainKey;
}

// See usage
// If different sort orders are needed, please refactor with names and comments
const ORDERED_RUNTIMES: ReadonlyArray<ChainRuntime> = [
  'evm',
  'svm',
  'sui',
  'near',
  'nearIntents',
];
// To be used in .sort() functions
export function compareRuntimes(a: ChainRuntime, b: ChainRuntime): number {
  const rankA = ORDERED_RUNTIMES.indexOf(a);
  const rankB = ORDERED_RUNTIMES.indexOf(b);
  return rankA - rankB;
}
